import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { Divider, Grid, IconButton } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import * as yup from "yup";

import { getSocketConnection, socket } from "../../socket";
import useSnackbarAlert from "../../utils/snackbarHandlers";
import CommonButton from "../common/button/Button";
import CommonInput from "../common/input/InputBox";
import logoTop from "../common/svgs/logoTop.svg";
import "./login.css";

const LoginForm: React.FC<any> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const [loading, setLoading] = React.useState<boolean>(false);
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();
  const [showContactInformation, setShowContactInformation] = React.useState<boolean>(false);

  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";

  const validationSchema = yup.object().shape({
    email: yup.string().email(t("invalidEmail")).required(t("emailRequired")),
    password: yup
      .string()
      .min(6, t("passwordInvalidError"))
      .max(20, t("passwordInvalidError"))
      .required(t("passwordRequired")),
  });

  const onHandleSubmit = (value: any) => {
    setLoading(true);
    const body = {
      email: value.email,
      password: value.password,
    };
    axios
      .post(`${apiBaseURL}/api/users/login`, body)
      .then(async (data: any) => {
        let localData = {
          fullName: data?.data?.fullName,
          email: data?.data?.email,
          role: data?.data?.role,
          uId: data?.data?.uId,
          storename: data?.data?.storename,
          ticketslocation: data?.data?.ticketslocation,
        };
        await localStorage.setItem("UserData", JSON.stringify(localData));
        let paymentStatus = "";
        if (data?.data?.role !== "ADMIN") {
          await axios
            .get(
              `${apiBaseURL}/api/subscription/fetchShopSubscriptionDetails?id=${data?.data?.uId}`,
            )
            .then(async (response: any) => {
              await localStorage.setItem(
                "subscription",
                JSON.stringify(response?.data?.subscription),
              );
              await localStorage.setItem(
                "userSubscriptionDetails",
                JSON.stringify(response?.data?.userSubscriptionDetails),
              );
            });
        }

        if (localStorage.getItem("UserData")) {
          console.log("Inside if statement");
          if (data?.data?.role === "SHOPOWNER") {
            navigate("/selection");
          } else {
            navigate("/admin/dashboard");
          }
        }
      })
      .catch((err: any) => {
        showErrorMessage(err?.response?.data?.message ?? t("invalidUserCredentials"));
        console.log("err", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit: onHandleSubmit,
    validationSchema: validationSchema,
    enableReinitialize: true,
  });
  const { values, errors, handleSubmit, touched, handleChange } = formik;

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.focus();
    }
  };

  return (
    <Grid container direction={"column"} className='flex flex-col justify-between min-h-screen'>
      {/* Start of header */}
      <header className='bg-[#074470] text-white p-4'>
        <div className='container mx-auto flex justify-center'>
          {/* <h1 className="text-2xl font-bold">Digi Tech Express</h1> */}
          <img src={logoTop} alt='logo top' height='30%' className="logo" width='auto' onClick={() => navigate('/')} style={{
            cursor: "pointer"
          }} />
        </div>
      </header>
      {/* End of header */}

      {/* Start of login form */}
      <Grid className='flex justify-center items-center flex-grow bg-[#E9E7FF]'>
        <Grid item xs={12} sm={8} md={6} lg={4} className='flex flex-col gap-[15px] w-[40%] mb-[5%] scale-[0.8]'>
          {/* <Grid className="text-4xl font-[700] flex justify-center font-['IBM_Plex_Sans']">
            {t("lotterySystem")}
          </Grid> */}
          <Grid className="bg-white rounded-[20px] py-[4%] font-['Baloo_2']">
            <Grid className='flex justify-center w-full text-center'>
              <Grid className='text-3xl font-[700] text-[#074470] w-[90%]'>{t("logIn")}</Grid>
            </Grid>
            <Grid className='w-full flex justify-center mt-2'>
              <Grid className='w-[90%]'>
                <CommonInput
                  id='email-id-input-box'
                  fullWidth
                  name='email'
                  label={t("email")}
                  value={values?.email}
                  onChange={handleChange}
                  placeholder={t("emailAddress")}
                  error={touched?.email && errors?.email ? true : false}
                  helperText={touched?.email && errors?.email ? errors?.email : ""}
                  className='login-input-box'
                />
              </Grid>
            </Grid>
            <Grid className='w-full flex justify-center'>
              <Grid className='w-[90%]'>
                <CommonInput
                  id='password-id-input-box'
                  fullWidth
                  type='password'
                  name='password'
                  label={t("password")}
                  placeholder={t("password")}
                  linkBesideLabel={true}
                  value={values?.password}
                  onChange={handleChange}
                  link={"/forgotPassword"}
                  linkName={t("forgotPassword")}
                  error={touched?.password && errors?.password ? true : false}
                  helperText={errors?.password && touched?.password ? errors?.password : ""}
                  className='login-input-box'
                />
              </Grid>
            </Grid>
            <Grid className='w-full flex justify-center'>
              <Grid className='w-[90%]'>
                <CommonButton
                  id='login-button'
                  className='login-button'
                  variant='contained'
                  ignoreButtonRootClass={true}
                  onClick={() => {
                    handleSubmit();
                  }}
                  style={{
                    backgroundColor: "#F17400",
                    height: "2.5rem",
                  }}
                  disabled={loading}
                >
                  {t("logIn")}
                  {loading && (
                    <CircularProgress
                      color='inherit'
                      size={14}
                      thickness={6}
                      style={{ marginLeft: "8px" }}
                    />
                  )}
                </CommonButton>
              </Grid>
            </Grid>

            <Divider className='my-4 w-[90%] ml-[5%]' />
            <Grid className='flex justify-center items-center'>
              <span className='flex-grow text-center mr-2 text-xl'>{t("dontHaveAnAccount")}</span>
              <CommonButton
                id='contact-us-button'
                variant='outlined'
                size='large'
                style={{
                  minWidth: "auto",
                  padding: "4px 8px",
                  width: "30%",
                  borderColor: "black",
                  color: "white",
                  marginRight: "5%",
                  backgroundColor: showContactInformation ? "#F17400" : "#074470",
                  borderRadius: "10px",
                }}
                onClick={() => {
                  setShowContactInformation(!showContactInformation);
                }}
                ignoreButtonRootClass={true}
              >
                {t("contactUs")}
              </CommonButton>
            </Grid>

            {showContactInformation && (
              <Grid container className='flex justify-center items-center'>
                <Grid item sm={12} className='flex justify-center items-center'>
                  <span className='text-gray-500 font-semibold text-xl'>CALL: </span>{" "}
                  <span className='text-black font-semibold text-xl'>&nbsp; +1 (833)-314-1825</span>
                </Grid>
                <Grid item sm={12} className='flex justify-center items-center'>
                  <span className='text-gray-500 font-semibold text-xl'>EMAIL: </span>{" "}
                  <span className='text-black font-semibold text-xl'>
                    &nbsp; info@digitechexpress.com
                  </span>
                </Grid>
              </Grid>
            )}

            {/* <Grid className='w-full flex justify-center'>
            <Grid className='w-[90%]'>
              <Divider className='divider' />
            </Grid>
          </Grid>
          <Grid className='flex justify-center w-full'>
            <Grid className='flex justify-between w-[90%] items-baseline'>
              <Grid className='text-[#474646] h-full'>{t("dontHaveAnAccount")}</Grid>
              <Grid>
                <CommonButton
                  id='login-button'
                  onClick={(e) => navigate("/signUp")}
                  className='sign-up-button'
                  variant='outlined'
                >
                  {t("signUp")}
                </CommonButton>
              </Grid>
            </Grid>
          </Grid> */}
          </Grid>
        </Grid>
      </Grid>
      {/* End of login form */}

      {/* Start of footer */}
      {/* Footer */}
      <footer className='bg-[#074470] text-white p-4 mt-auto'>
        <div className='container mx-auto flex justify-between items-center'>
          <p className='text-center'>
            Copyright &copy; 2024-2030 Digi Tech Express | All Rights Reserved
          </p>
          <div className='flex gap-4'>
            <IconButton
              aria-label='Facebook'
              color='inherit'
              size='small'
              onClick={() => openInNewTab("https://www.facebook.com/profile.php?id=61556226006007")}
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              aria-label='Twitter'
              color='inherit'
              size='small'
              onClick={() => openInNewTab("https://www.instagram.com/digitech.express")}
            >
              <InstagramIcon />
            </IconButton>
            <IconButton
              aria-label='LinkedIn'
              color='inherit'
              size='small'
              onClick={() => openInNewTab("https://www.youtube.com/@digitechexpress")}
            >
              <YouTubeIcon />
            </IconButton>
          </div>
        </div>
      </footer>
      {/* End of footer */}
    </Grid>
  );
};
export default LoginForm;
